import { Link } from 'react-router-dom'

const guide = {
  slug: 'how-to-block-porn-on-safari',
  category: 'device',
  kicker: 'Safari · iPhone, iPad, Mac',
  title: 'How to block porn on Safari, and why blocking the browser is never enough',
  seoTitle: 'How to Block Porn on Safari (iPhone, iPad, Mac)',
  description: `Limit Adult Websites, an allow-list, private browsing off: how to block porn in Safari on iPhone, iPad and Mac, what each setting really covers, and what it misses.`,
  published: '2026-09-03',
  minutes: 7,
  intro: `Safari is where most people go looking, so Safari is the first thing
          people try to lock. Apple gives you real tools for that, and they are
          worth turning on. But a browser filter answers the wrong question:
          it locks one door in a house full of them. Here is how to block porn
          in Safari on every Apple device, and then how to block it under
          Safari, where it counts.`,
  next: ['how-to-block-porn-on-iphone', 'does-incognito-bypass-porn-blockers', 'is-screen-time-enough-to-block-porn'],
  body: (
    <>
      <h2>Safari on iPhone and iPad: Limit Adult Websites</h2>
      <ol>
        <li>Open <b>Settings → Screen Time</b>, turn it on.</li>
        <li>Tap <b>Content &amp; Privacy Restrictions</b>, enable them.</li>
        <li><b>Content Restrictions → Web Content → Limit Adult Websites</b>.</li>
        <li>Set a <b>Screen Time passcode</b>.</li>
      </ol>
      <p>
        Two things happen. Safari starts refusing sites Apple classifies as
        adult, and <b>Private Browsing disappears</b> from Safari's tab switcher
        while the restriction is on. That second part is the useful one: the
        private tab was the place the block used to be forgotten.
      </p>
      <p>
        Under <b>Never Allow</b> you can add sites Apple's list misses. Under
        <b>Always Allow</b> you can unblock something legitimate that got
        caught.
      </p>

      <h2>The stricter option: Allowed Websites Only</h2>
      <p>
        In the same <b>Web Content</b> screen, <b>Allowed Websites Only</b>
        turns Safari into an allow-list: nothing loads except the sites you
        listed. It is the most reliable setting Apple ships, and it is also
        unlivable for most adults, because the web you actually need is
        larger than any list. It works for a child's device. For your own,
        most people turn it off within a week, which is the problem with
        every setting you can turn off.
      </p>

      <h2>Safari on Mac: the same filter, from System Settings</h2>
      <ol>
        <li><b>System Settings → Screen Time → Content &amp; Privacy</b>, turn it on.</li>
        <li>Under <b>Web Content</b>, choose <b>Limit Adult Websites</b> (or <b>Allowed Websites Only</b>).</li>
        <li>Set a Screen Time passcode. If your Mac and your iPhone use the same Apple ID, <b>Share Across Devices</b> applies it everywhere.</li>
      </ol>
      <p>
        On Mac there is one more Safari-specific option: extensions. Safari
        supports content-blocking extensions from the App Store, and some of
        them filter adult sites. They live inside Safari, and they are removed
        from Safari's Extensions settings in two clicks.
      </p>

      <h2>What a Safari block does not cover</h2>
      <p>
        This is the part that matters more than the steps. A Safari filter is
        a filter <em>in Safari</em>. It doesn't see:
      </p>
      <ul>
        <li><b>Other browsers.</b> Chrome, Firefox, Brave, DuckDuckGo, Edge are a free download, and on iPhone they don't inherit Safari's restriction unless the whole device is restricted.</li>
        <li><b>In-app browsers.</b> The web view inside Instagram, X, Reddit, Telegram or a mail app is not Safari.</li>
        <li><b>Apps that aren't browsers.</b> A video app or a social app that serves adult content doesn't go through a web filter at all.</li>
        <li><b>The person who holds the passcode.</b> If that's you, the filter holds as long as you feel fine. A Screen Time passcode can also be reset with the Apple ID on the device. The five ways around it are in <Link to="/guides/is-screen-time-enough-to-block-porn">is Screen Time enough</Link>.</li>
      </ul>
      <p>
        Apple's "Limit Adult Websites" does apply device-wide to apps that use
        Apple's web view, and it's a good design. But the moment you compare
        "blocks Safari" with "blocks the device", you've found the real
        question.
      </p>

      <h2>Block it under Safari: DNS, then supervision</h2>
      <p>
        Every app on the device, Safari included, asks the same resolver
        "where is this domain?" before it loads anything. A <b>DNS category
        filter</b> refuses pornography at that step, so the site never
        resolves in Safari, in Chrome, in a private tab, in an in-app browser,
        or in an app that isn't a browser at all. The browser is no longer a
        variable. How the mechanism works, in full:
        <Link to="/guides/dns-porn-blocker"> DNS porn blockers explained</Link>.
      </p>
      <p>
        A DNS filter installed as a profile can still be removed in four taps,
        and a VPN can route around it. That's why SELFCNTRL adds
        <b> supervision</b> on iPhone and iPad: the profile becomes
        non-removable, adding a VPN is forbidden by the profile itself, and
        loosening anything waits twenty-four hours from your account.
        The setup, ten minutes once, is in
        <Link to="/guides/how-to-block-porn-on-iphone"> the iPhone guide</Link>
        and <Link to="/guides/how-to-block-porn-on-ipad"> the iPad guide</Link>.
        On Mac, the filter installs as an app with curfews and the same
        delay: <Link to="/guides/how-to-block-porn-on-mac">the Mac guide</Link>.
      </p>

      <h2>Safe search inside Safari</h2>
      <p>
        Blocking sites doesn't clean up search results or image search.
        SELFCNTRL enforces safe search at the DNS level, so Google, Bing,
        DuckDuckGo and YouTube return their filtered results in Safari and in
        every other browser, without a setting you could toggle back.
      </p>

      <h2>Side by side</h2>
      <table>
        <thead>
          <tr><th></th><th>Limit Adult Websites</th><th>Allowed Websites Only</th><th>DNS filter</th><th>DNS + supervised</th></tr>
        </thead>
        <tbody>
          <tr><td>Blocks porn in Safari</td><td>Most of it</td><td>Yes</td><td>Yes</td><td>Yes</td></tr>
          <tr><td>Blocks other browsers and apps</td><td>Partly</td><td>Partly</td><td>Yes</td><td>Yes</td></tr>
          <tr><td>Private browsing</td><td>Removed</td><td>Removed</td><td>Irrelevant</td><td>Irrelevant</td></tr>
          <tr><td>Livable for an adult</td><td>Yes</td><td>Rarely</td><td>Yes</td><td>Yes</td></tr>
          <tr><td>Off in under a minute</td><td>Yes</td><td>Yes</td><td>Yes</td><td>No</td></tr>
        </tbody>
      </table>
    </>
  ),
  faq: [
    {
      q: 'Does Limit Adult Websites block private browsing in Safari?',
      a: `Yes. While the restriction is on, Safari's Private Browsing option
          is removed from the tab switcher on iPhone and iPad. It comes back
          the moment the restriction is turned off, which is why the passcode
          matters more than the setting.`
    },
    {
      q: 'Can I block a specific site in Safari?',
      a: `Yes. Under Screen Time → Content Restrictions → Web Content, add the
          domain to Never Allow. It applies to Safari and to apps that use
          Apple's web view. For a block that applies to every app on the
          device, add the domain from your SELFCNTRL account instead: it's
          refused at DNS level, and blocking more takes effect immediately.`
    },
    {
      q: 'Is there a porn blocker extension for Safari?',
      a: `On Mac, yes: Safari accepts content-blocking extensions from the App
          Store. On iPhone and iPad, Safari extensions exist but a content
          blocker there is still inside Safari, and it's removed from Safari's
          settings in a couple of taps. An extension is a filter for one
          browser; the device has many.`
    },
    {
      q: 'What if I use Chrome on my iPhone instead of Safari?',
      a: `Then a Safari-only setting does nothing for you. Screen Time's web
          content restriction reaches some apps but not every browser. A DNS
          filter reaches all of them, because it works before any browser
          gets a say.`
    }
  ]
}

export default guide
